import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { DEMO_KEY_RATE_PER_MINUTE, planHasHistoryApi } from "@currencycore/shared";
import { API_BASE, getDemoKey, getOrgPlans, type DemoKeyResponse } from "../lib/api.ts";
import { demoKeyExpiringSoon } from "../lib/demo-key.ts";
import {
  ENDPOINTS,
  buildPath,
  getApiVersions,
  getEndpoint,
  latestVersion,
  type CatalogEndpoint,
  type EndpointId,
} from "../lib/api-catalog.ts";
import { Select } from "../components/Select.tsx";
import { DatePicker } from "../components/DatePicker.tsx";
import { CodeSamples } from "../components/CodeSamples.tsx";
import { RequestParamTable, ResponseFieldTable } from "../components/ParamTable.tsx";
import { trackPlaygroundQuery } from "../lib/analytics.ts";

const MASKED_KEY = "cc_demo_••••••••";

/** Today as a UTC ISO date — the API treats `date` as UTC and rejects the future. */
const todayIso = () => new Date().toISOString().slice(0, 10);

/** Seed a param-value map from each endpoint's example values. */
function seedValues(endpoint: CatalogEndpoint): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of endpoint.params) out[p.name] = p.example;
  return out;
}

/** Example /ai prompts, grouped by capability — shown in the "Sample questions" popup. */
const SAMPLE_QUESTIONS: { category: string; questions: string[] }[] = [
  {
    category: "Simple conversion",
    questions: ["Convert 50 EUR to INR", "How much is 100 USD in AED?", "Convert 10,000 JPY to GBP"],
  },
  {
    category: "Historical conversion",
    questions: [
      "Convert 100 EUR to INR using the rate from January 1st 2020",
      "How much was 1000 AED worth in INR five years ago?",
      "Convert 50 USD to EUR using yesterday's exchange rate",
      "What was the EUR/USD rate during COVID in March 2020?",
    ],
  },
  {
    category: "Trend analysis",
    questions: [
      "How has EUR performed against USD over the last year?",
      "Has INR strengthened or weakened against USD since 2015?",
      "Show the trend of AED against INR over the last decade",
      "What is the percentage change in EUR/USD over the last 5 years?",
      "Which month saw the biggest change in EUR/USD during the last year?",
    ],
  },
  {
    category: "Comparison",
    questions: [
      "Compare EUR, GBP and AED against USD over the last 10 years",
      "Which currency performed best against INR since 2010?",
      "Compare EUR and GBP performance during the last recession",
      "Which has been more stable over the last 5 years: AED or EUR?",
      "Between EUR, GBP and JPY, which gained the most value against USD this year?",
    ],
  },
  {
    category: "Ranking",
    questions: [
      "What were the top 10 performing currencies against USD in 2025?",
      "Which currencies lost the most value against INR in the last decade?",
      "Show the strongest currencies against EUR over the last 20 years",
      "Rank GCC currencies by performance against USD since 2010",
    ],
  },
  {
    category: "PPP queries",
    questions: [
      "How much purchasing power does $100 in the US represent in India?",
      "Convert $500 US dollars into PPP-adjusted Indian rupees",
      "Which country offers the highest purchasing power for a software engineer earning $50,000?",
      "Compare nominal exchange rate and PPP exchange rate for India",
      "Has India's PPP adjustment improved over the last decade?",
    ],
  },
  {
    category: "Multi-step reasoning",
    questions: [
      "I earn $3,000 per month in Dubai. What would that be equivalent to in Bangalore after PPP adjustment?",
      "I converted 1000 EUR to USD in 2019 and held the USD until today. Would I have gained or lost value?",
      "I'm moving from London to Bangalore. How much salary would I need to maintain the same purchasing power?",
      "Compare the purchasing power of ₹1 lakh per month in Bangalore with salaries in Dubai and London",
    ],
  },
  {
    category: "Agent workflow",
    questions: [
      "Find the strongest currency against USD over the last 10 years and summarize the reasons",
      "Compare EUR, GBP and INR over the last decade and generate a short report",
      "Identify currencies that have remained within ±5% of USD over the last 5 years",
      "Find the most volatile major currency pair in the last year",
      "Generate an investment-style summary of EUR/USD performance since 2020",
    ],
  },
  {
    category: "Sample query",
    questions: ["Compare EUR, GBP and AED against USD over the last 10 years and tell me which performed best."],
  },
];

interface RunResult {
  status: number;
  ok: boolean;
  body: string; // pretty-printed JSON (or raw text)
  ms: number; // round-trip time
  method: string;
  url: string;
}

function statusBadgeClass(status: number): string {
  if (status >= 200 && status < 300) return "badge-ok";
  if (status >= 400 && status < 500) return "badge-warn";
  if (status >= 500) return "badge-danger";
  return "badge-neutral";
}

export function PlaygroundPage() {
  const { orgId } = useParams<{ orgId: string }>();

  // --- API versions (env-driven; default-select the latest) -----------------
  const versions = useMemo(() => getApiVersions(), []);
  const [version, setVersion] = useState<string>(() => latestVersion(versions));

  // --- Endpoint + per-endpoint param values ---------------------------------
  const [endpointId, setEndpointId] = useState<EndpointId>("convert");
  const endpoint = getEndpoint(endpointId);
  // Keep a value map per endpoint so switching back and forth preserves edits.
  const [valuesByEndpoint, setValuesByEndpoint] = useState<Record<string, Record<string, string>>>(
    () => {
      const init: Record<string, Record<string, string>> = {};
      for (const e of ENDPOINTS) init[e.id] = seedValues(e);
      return init;
    },
  );
  const values = valuesByEndpoint[endpointId] ?? {};

  const setValue = useCallback(
    (name: string, v: string) => {
      setValuesByEndpoint((prev) => ({
        ...prev,
        [endpointId]: { ...(prev[endpointId] ?? {}), [name]: v },
      }));
    },
    [endpointId],
  );

  // --- Demo key provisioning -------------------------------------------------
  const [demo, setDemo] = useState<DemoKeyResponse | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [keyLoading, setKeyLoading] = useState(true);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showSamples, setShowSamples] = useState(false);

  // Hold the latest demo key in a ref so async run logic never reads stale state.
  const demoRef = useRef<DemoKeyResponse | null>(null);
  demoRef.current = demo;

  /** Copy the response body (pretty-printed JSON or raw text) to the clipboard. */
  function copyResponse() {
    if (!result) return;
    void navigator.clipboard?.writeText(result.body).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  }

  // Fetch a demo key. Updates state + ref and returns it (or null on failure).
  const fetchKey = useCallback(async (): Promise<DemoKeyResponse | null> => {
    if (!orgId) return null;
    try {
      const res = await getDemoKey(orgId);
      setDemo(res);
      demoRef.current = res;
      setKeyError(null);
      return res;
    } catch (err) {
      setKeyError(err instanceof Error ? err.message : "Couldn't provision a demo key.");
      return null;
    }
  }, [orgId]);

  // Provision the demo key on mount.
  useEffect(() => {
    let active = true;
    setKeyLoading(true);
    void fetchKey().finally(() => {
      if (active) setKeyLoading(false);
    });
    return () => {
      active = false;
    };
  }, [fetchKey]);

  // --- Current plan (for entitlement gating of plan-restricted endpoints) ----
  // Mirrors PlansPage: fetch the org's plans and read `current.planId`. Stays
  // null until known so we never wrongly block an entitled user.
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getOrgPlans(orgId)
      .then((data) => {
        if (active) setCurrentPlan(data.current.planId);
      })
      .catch(() => {
        /* leave null — treat unknown as allowed (no false negatives) */
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  // The selected endpoint is gated when it declares a minimum plan tier and the
  // org's known plan lacks the history-API entitlement. `null` plan → not gated.
  const gated = !!endpoint.minPlanTier && currentPlan != null && !planHasHistoryApi(currentPlan);

  // --- Built request --------------------------------------------------------
  const path = buildPath(endpoint, version, values);
  const fullUrl = `${API_BASE}${path}`;

  // Text/number/date inputs render in the field grid; booleans render as
  // labeled toggle rows below it.
  const inputParams = endpoint.params.filter((p) => p.type !== "boolean");
  const boolParams = endpoint.params.filter((p) => p.type === "boolean");

  // Perform the actual API call. Public endpoints send no Authorization header
  // (the key the demo would attach is ignored there anyway).
  async function callApi(key?: string): Promise<Response> {
    const headers: Record<string, string> = {};
    if (!endpoint.public && key) headers.Authorization = `Bearer ${key}`;
    return fetch(`${API_BASE}${path}`, { headers, credentials: "include" });
  }

  async function handleRun() {
    if (running) return;
    setRunning(true);
    setRunError(null);
    setResult(null);

    const startedAt = performance.now();
    try {
      // 1. Ensure a fresh demo key. Public endpoints need none. We rotate
      //    PROACTIVELY when the held key is missing, expired, or near expiry, so a
      //    stale key is never sent (the server also rotates ahead of expiry). The
      //    initial provision may still be mid-flight, so this doubles as on-demand.
      let active = demoRef.current;
      if (!endpoint.public && (!active || demoKeyExpiringSoon(active.expiresAt))) {
        active = (await fetchKey()) ?? active;
      }
      if (!endpoint.public && !active) {
        setRunError(keyError ?? "Couldn't provision a demo key. Please try again.");
        return;
      }

      // 2. Call the API; on 401 (a key that lapsed anyway) re-fetch + retry once
      //    as a safety net. Public endpoints can't 401 on auth, so no retry needed.
      let res = await callApi(active?.key);
      if (!endpoint.public && res.status === 401) {
        const refreshed = await fetchKey();
        if (refreshed) res = await callApi(refreshed.key);
      }

      // 3. Read + pretty-print the body.
      const text = await res.text();
      let pretty = text;
      try {
        pretty = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        /* not JSON — show raw text */
      }

      if (!res.ok) {
        // Surface the API's error.message when present (e.g. 429 rate limit).
        let message = `Request failed (${res.status}).`;
        try {
          const parsed = JSON.parse(text) as { error?: { message?: string } };
          if (parsed.error?.message) message = parsed.error.message;
        } catch {
          /* keep default */
        }
        if (res.status === 429) {
          setRunError(message || `Rate limit reached. The demo key allows ${DEMO_KEY_RATE_PER_MINUTE} requests/minute. Wait a moment and try again.`);
        } else {
          setRunError(message);
        }
      }

      const ms = Math.round(performance.now() - startedAt);
      setResult({
        status: res.status,
        ok: res.ok,
        body: pretty,
        ms,
        method: endpoint.method,
        url: fullUrl,
      });
      trackPlaygroundQuery(endpointId, res.status, ms);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : "Network error: the request didn't complete.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="animate-fade-in">
      {/* Page header */}
      <div className="page-head rise">
        <div>
          <h1>Playground</h1>
          <p className="sub">Try the public APIs live from your browser. No setup, no key to copy.</p>
        </div>
      </div>

      {/* Sandbox banner */}
      <div className="notice accent rise" style={{ marginBottom: "18px" }}>
        <span className="ic">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
          </svg>
        </span>
        <span>
          The Playground uses a <strong style={{ color: "var(--text)" }}>sandbox demo key</strong>,
          rate-limited to <strong style={{ color: "var(--text)" }}>{DEMO_KEY_RATE_PER_MINUTE} requests/min</strong>{" "}
          and bound to your signed-in session. Not for production; managed automatically and rotates when it expires.
        </span>
      </div>

      {keyError && (
        <div className="error-banner" style={{ marginBottom: "18px" }}>{keyError}</div>
      )}

      <div className="card rise">
        <div className="card-head">
          <h2>Request composer</h2>
          {endpoint.public ? (
            <span className="badge badge-info">Public · no key</span>
          ) : (
            <span className="badge badge-neutral">
              <span className="mono">{MASKED_KEY}</span>
            </span>
          )}
        </div>
        <div className="card-pad">
          {/* Version + endpoint pickers */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 160px) minmax(200px, 320px)", gap: "16px", marginBottom: "16px" }}>
            <div className="field">
              <label htmlFor="pg-version">API version</label>
              <Select
                value={version}
                ariaLabel="API version"
                options={versions.map((v) => ({ value: v, label: v }))}
                onChange={setVersion}
              />
              <span className="help">Latest selected by default.</span>
            </div>
            <div className="field">
              <label htmlFor="pg-endpoint">Endpoint</label>
              <Select
                value={endpointId}
                ariaLabel="Endpoint"
                options={ENDPOINTS.map((e) => ({ value: e.id, label: e.label }))}
                onChange={(v) => setEndpointId(v as EndpointId)}
              />
              <span className="help">{endpoint.summary}</span>
            </div>
          </div>

          {/* Plan-gated endpoint (e.g. /history needs Growth+). The endpoint stays
              selectable; we just explain the gate and disable Run below. */}
          {gated && (
            <div className="notice" style={{ marginBottom: "16px", borderColor: "var(--warn, var(--accent-line))" }}>
              <span className="ic">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" />
                </svg>
              </span>
              <span>
                Upgrade to the Growth plan or higher to use the <code>/history</code> endpoint.{" "}
                <a href={`/dashboard/${orgId ?? ""}/plans`} style={{ color: "var(--accent-text)" }}>
                  View plans →
                </a>
              </span>
            </div>
          )}

          {/* AI runs on the separate credit pool — make the cost model clear. */}
          {endpointId === "ai" && (
            <div className="notice accent rise" style={{ marginBottom: "16px" }}>
              <span className="ic">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
                </svg>
              </span>
              <span>
                The <code>/ai</code> endpoint runs on <strong style={{ color: "var(--text)" }}>AI credits</strong>.
                Each AI request consumes <strong style={{ color: "var(--text)" }}>1 AI credit</strong> (a separate pool
                from your monthly request quota), including calls from the Playground.{" "}
                <a href="https://currency-core.com/docs/ai-credits" target="_blank" rel="noreferrer" style={{ color: "var(--accent-text)" }}>
                  Learn about AI credits →
                </a>
              </span>
            </div>
          )}

          {/* Endpoint-specific inputs (generated from catalog params). Text /
              number / date inputs flow in a grid; boolean params render as
              labeled toggle rows below so they don't float inside the grid. */}
          {endpoint.params.length > 0 ? (
            <>
              {inputParams.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 240px))", gap: "16px", justifyContent: "start" }}>
                  {inputParams.map((p) => {
                    const id = `pg-${endpoint.id}-${p.name}`;
                    return (
                      <div key={p.name} className="field">
                        <label htmlFor={id}>
                          {p.name}
                          {p.required && <span style={{ color: "var(--danger)" }}> *</span>}
                          {p.in === "path" && (
                            <span className="badge badge-neutral" style={{ marginLeft: "8px" }}>path</span>
                          )}
                        </label>
                        {p.name === "date" ? (
                          <DatePicker
                            value={values[p.name] ?? ""}
                            max={todayIso()}
                            ariaLabel={p.name}
                            onChange={(v) => setValue(p.name, v)}
                          />
                        ) : (
                          <input
                            id={id}
                            className="input"
                            type={p.type === "number" ? "number" : "text"}
                            inputMode={p.type === "number" ? "decimal" : undefined}
                            value={values[p.name] ?? ""}
                            onChange={(e) => setValue(p.name, e.target.value)}
                            placeholder={p.example || undefined}
                            autoComplete="off"
                          />
                        )}
                        <span className="help">{p.description}</span>
                        {p.name === "q" && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setShowSamples(true)}
                            style={{ marginTop: "8px", alignSelf: "start" }}
                          >
                            💡 Sample questions
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {boolParams.length > 0 && (
                <div className="pg-toggles">
                  {boolParams.map((p) => {
                    const id = `pg-${endpoint.id}-${p.name}`;
                    return (
                      <div key={p.name} className="pg-toggle">
                        <label className="switch">
                          <input
                            id={id}
                            type="checkbox"
                            checked={(values[p.name] ?? "") === "true"}
                            onChange={(e) => setValue(p.name, e.target.checked ? "true" : "false")}
                          />
                          <span className="track" />
                          <span className="thumb" />
                        </label>
                        <div className="pg-toggle-text">
                          <label className="pg-toggle-name" htmlFor={id}>
                            {p.name}
                            {p.required && <span style={{ color: "var(--danger)" }}> *</span>}
                          </label>
                          <span className="help">{p.description}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p className="muted" style={{ fontSize: "13px", margin: 0 }}>
              No parameters: {endpoint.summary}
            </p>
          )}

          {/* Built request URL */}
          <div className="field" style={{ marginTop: "18px" }}>
            <label>Request URL</label>
            <div className="secret-box">
              <code style={{ flex: 1, minWidth: 0, overflowX: "auto", whiteSpace: "nowrap" }}>
                {endpoint.method} {fullUrl}
              </code>
            </div>
          </div>

          {/* Run */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "18px" }}>
            <button className="btn btn-primary" onClick={() => void handleRun()} disabled={running || gated || (!endpoint.public && keyLoading)}>
              {running ? <><span className="spinner" />Running…</> : !endpoint.public && keyLoading ? <><span className="spinner" />Provisioning…</> : "Run"}
            </button>
            <span className="hint">
              {gated
                ? "Requires the Growth plan or higher."
                : endpoint.public
                  ? "Public endpoint, runs without a key."
                  : "Sends the request with your demo key + session."}
            </span>
          </div>
        </div>
      </div>

      {/* Structured response */}
      {(result || runError) && (
        <div className="card mt rise">
          <div className="card-head">
            <h2>Response</h2>
            {result && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span className={`badge ${statusBadgeClass(result.status)}`}>
                  <span className="dot" />{result.status}
                </span>
                <span className="mono" style={{ fontSize: "12px", color: "var(--text-subtle)" }}>{result.ms} ms</span>
                <button className="btn btn-sm" type="button" onClick={copyResponse} aria-label="Copy response">
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            )}
          </div>
          <div className="card-pad">
            {result && (
              <div
                className="secret-box"
                style={{ marginBottom: "14px", display: "flex", gap: "8px", alignItems: "center" }}
              >
                <span className="badge badge-info">{result.method}</span>
                <code style={{ flex: 1, minWidth: 0, overflowX: "auto", whiteSpace: "nowrap" }}>{result.url}</code>
                <span className={`badge ${statusBadgeClass(result.status)}`}>{result.status}</span>
              </div>
            )}
            {runError && (
              <div className="notice" style={{ marginBottom: result ? "14px" : 0, borderColor: "var(--danger)", color: "var(--text)" }}>
                <span className="ic" style={{ color: "var(--danger)" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" />
                  </svg>
                </span>
                {runError}
              </div>
            )}
            {result && (
              <pre
                className="mono"
                aria-live="polite"
                style={{ margin: 0, overflowX: "auto", fontSize: "12.5px", lineHeight: 1.7, color: "var(--text-muted)", maxHeight: "440px" }}
              >
                {result.body}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Info: request params + response fields */}
      <div className="card mt rise">
        <div className="card-head">
          <h2>Definitions</h2>
          <span className="badge badge-neutral">{endpoint.method} {endpoint.pathTemplate.replace("{version}", version)}</span>
        </div>
        <div className="card-pad">
          <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", margin: "0 0 12px" }}>Request parameters</h3>
          <RequestParamTable params={endpoint.params} />
          <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", margin: "22px 0 12px" }}>Response fields</h3>
          <ResponseFieldTable fields={endpoint.response} />
        </div>
      </div>

      {/* Code samples for the built request */}
      <CodeSamples method={endpoint.method} url={fullUrl} requiresKey={!endpoint.public} />

      {/* Sample-question picker for the /ai prompt — click a question to fill `q`.
          Rendered in a portal at document.body so it centers on the viewport,
          unaffected by any ancestor transform/overflow. */}
      {showSamples && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Sample questions"
          onClick={() => setShowSamples(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 1000, padding: "20px",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "color-mix(in oklch, var(--bg, #000) 70%, transparent)",
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: "660px", maxHeight: "82vh", overflowY: "auto" }}
          >
            <div className="card-head">
              <h2>Sample questions</h2>
              <button className="btn btn-sm" type="button" onClick={() => setShowSamples(false)} aria-label="Close">
                Close
              </button>
            </div>
            <div className="card-pad">
              <p className="muted" style={{ marginTop: 0, fontSize: "12.5px" }}>
                Pick one to drop it into the <code>q</code> field, then Run.
              </p>
              {SAMPLE_QUESTIONS.map((group) => (
                <div key={group.category} style={{ marginBottom: "18px" }}>
                  <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", margin: "0 0 8px" }}>
                    {group.category}
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {group.questions.map((qn) => (
                      <button
                        key={qn}
                        type="button"
                        onClick={() => {
                          setValue("q", qn);
                          setShowSamples(false);
                        }}
                        style={{
                          textAlign: "left", padding: "8px 11px", fontSize: "13px", cursor: "pointer",
                          border: "1px solid var(--border)", borderRadius: "9px",
                          background: "color-mix(in oklch, var(--surface-raised) 55%, transparent)",
                          color: "var(--text-muted)",
                        }}
                      >
                        {qn}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
